/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState } from 'react'
import { useIntl } from 'react-intl'

import {
  EuiButton,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiText,
  EuiTitle,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import genericMessages from '@modules/mfa-lib/genericMessages'
import type {
  MfaPasscodeChallenge,
  useVerifySaasCurrentUserMfaChallengeMutation,
} from '@modules/cloud-lib/users/hooks/mfa'

import { renderError } from './errorUtils'

const AuthenticatorForm: React.FunctionComponent<{
  verifyMutation: ReturnType<typeof useVerifySaasCurrentUserMfaChallengeMutation>
  onSubmit: (challenge: MfaPasscodeChallenge) => void
  isInvalidToken: boolean
}> = ({ verifyMutation, onSubmit, isInvalidToken }) => {
  const { formatMessage } = useIntl()

  const [value, setValue] = useState('')

  const title = (
    <EuiTitle size='m'>
      <h1 style={{ textAlign: 'center' }}>
        <FormattedMessage
          id='mfaLogin.authenticatorForm.title'
          defaultMessage='Check your authenticator app for a code'
        />
      </h1>
    </EuiTitle>
  )

  const instructions = (
    <EuiText size='s' textAlign='center' style={{ maxWidth: '20rem', margin: '0 auto' }}>
      <FormattedMessage
        id='mfaLogin.authenticatorForm.instructions'
        defaultMessage='Enter the 6-digit code from your registered authenticator app.'
      />
    </EuiText>
  )

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()

        onSubmit({ pass_code: value })
      }}
    >
      <EuiFlexGroup direction='column' alignItems='center'>
        <EuiFlexItem>{title}</EuiFlexItem>
        <EuiFlexItem>
          <EuiFlexGroup direction='column' alignItems='stretch' style={{ maxWidth: '24rem' }}>
            <EuiFlexItem>{instructions}</EuiFlexItem>
            <EuiFlexItem>
              <EuiFormRow hasChildLabel={false}>
                <EuiFieldText
                  aria-label={formatMessage(genericMessages.passCode)}
                  autoFocus={true}
                  autoComplete='off'
                  icon='lock'
                  value={value}
                  disabled={isInvalidToken}
                  onChange={(e) => {
                    setValue(e.target.value)
                  }}
                />
              </EuiFormRow>
              {verifyMutation.isError &&
                renderError(
                  isInvalidToken,
                  <FormattedMessage
                    id='mfaLogin.authenticatorForm.codeError'
                    defaultMessage='This code may be incorrect or expired. Try again.'
                  />,
                )}
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiButton
                isLoading={verifyMutation.isLoading}
                isDisabled={isInvalidToken}
                type='submit'
                fill={true}
              >
                <FormattedMessage {...genericMessages.verifyCode} />
              </EuiButton>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
      </EuiFlexGroup>
    </form>
  )
}

export default AuthenticatorForm
